// Maps an ASD Jobs API job object into a schema.org JobPosting object,
// ready to be JSON.stringify'd into a <script type="application/ld+json"> tag.
//
// NOTE: hiringOrganization is hardcoded to "Airswift" for now, per POC scope.
// Salary, date-timezone, and country-code handling are flagged inline below —
// these are the items to confirm with backend before this goes past POC.

const EMPLOYMENT_TYPE_MAP = {
  'full time': 'FULL_TIME',
  'part time': 'PART_TIME',
  contract: 'CONTRACTOR',
  contractor: 'CONTRACTOR',
  temporary: 'TEMPORARY',
  intern: 'INTERN',
  internship: 'INTERN',
  volunteer: 'VOLUNTEER',
  'per diem': 'PER_DIEM'
};

const SALARY_UNIT_MAP = {
  'per hour': 'HOUR',
  'per day': 'DAY',
  'per week': 'WEEK',
  'per month': 'MONTH',
  'per year': 'YEAR',
  annual: 'YEAR'
};

// Minimal country name -> ISO 3166-1 alpha-2 map, covering what we've seen
// in sample data so far. Extend as more countries show up, or swap for a
// proper lookup table/library once this moves past POC.
const COUNTRY_CODE_MAP = {
  india: 'IN',
  'united arab emirates': 'AE',
  uae: 'AE',
  'united states': 'US',
  usa: 'US',
  'united kingdom': 'GB',
  uk: 'GB'
};

const HARD_CODED_HIRING_ORG = 'Airswift';

function mapEmploymentType(jobType) {
  if (!jobType) return undefined;
  return EMPLOYMENT_TYPE_MAP[jobType.trim().toLowerCase()] || 'OTHER';
}

function mapCountryCode(country) {
  if (!country) return undefined;
  return COUNTRY_CODE_MAP[country.trim().toLowerCase()] || country;
}

function mapSalaryUnit(frequency) {
  if (!frequency) return undefined;
  return SALARY_UNIT_MAP[frequency.trim().toLowerCase()] || undefined;
}

// Sample data has shown placeholder-looking salary values (e.g. SalaryLow: 2,
// SalaryHigh: 0). Rather than publish clearly invalid pay data, we skip the
// baseSalary block entirely when the numbers don't make sense. Revisit once
// backend confirms whether these are real figures or test placeholders.
function isSalaryValid(low, high) {
  if (typeof low !== 'number' || typeof high !== 'number') return false;
  if (low <= 0 && high <= 0) return false;
  if (high > 0 && high < low) return false;
  return true;
}

function buildBaseSalary(job) {
  const { SalaryLow, SalaryHigh, SalaryFrequency, Currency } = job;
  if (!isSalaryValid(SalaryLow, SalaryHigh)) return undefined;

  const unitText = mapSalaryUnit(SalaryFrequency);

  return {
    '@type': 'MonetaryAmount',
    currency: Currency || undefined,
    value: {
      '@type': 'QuantitativeValue',
      minValue: SalaryLow,
      ...(SalaryHigh > 0 ? { maxValue: SalaryHigh } : {}),
      ...(unitText ? { unitText } : {})
    }
  };
}

function buildJobLocation(job) {
  return {
    '@type': 'Place',
    address: {
      '@type': 'PostalAddress',
      addressLocality: job.City || undefined,
      addressRegion: job.State || undefined,
      addressCountry: mapCountryCode(job.Country),
      ...(job.Postcode ? { postalCode: job.Postcode } : {})
    }
  };
}

export function buildJobPostingSchema(job) {
  if (!job) return null;

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: job.JobTitle,
    description: job.JobDescription,
    identifier: {
      '@type': 'PropertyValue',
      name: HARD_CODED_HIRING_ORG,
      value: String(job.Id ?? job.Guid ?? '')
    },
    // TODO: confirm with backend whether PublishedDate/JobExpiryDateTime are
    // UTC or local time — currently passed through as-is, with no offset.
    datePosted: job.PublishedDate,
    hiringOrganization: {
      '@type': 'Organization',
      name: HARD_CODED_HIRING_ORG
    },
    employmentType: mapEmploymentType(job.JobType),
    industry: job.Industry || undefined
  };

  if (job.JobExpiryDateTime) {
    schema.validThrough = job.JobExpiryDateTime;
  }

  if (job.RemotePosition) {
    schema.jobLocationType = 'TELECOMMUTE';
    schema.applicantLocationRequirements = {
      '@type': 'Country',
      name: job.Country || undefined
    };
  } else {
    schema.jobLocation = buildJobLocation(job);
  }

  const baseSalary = buildBaseSalary(job);
  if (baseSalary) {
    schema.baseSalary = baseSalary;
  }

  // If the job is closed, make sure validThrough reflects that rather than
  // leaving stale structured data claiming the job is still open.
  if (job.IsOpen === false && !job.JobExpiryDateTime) {
    schema.validThrough = new Date().toISOString();
  }

  return schema;
}